// Context provider for Firebase authentication state.
import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { subscribeToAuthChanges } from '@/services/authService';
import { reloadCurrentUser } from '@/services/authEmailVerification';
import { initAnalytics } from '@/services/analyticsService';
import { AuthContext } from '@/features/auth/AuthContext.def';

/**
 * Provider component for the authentication context.
 * Listens to Firebase auth state changes and makes the current user available.
 * Maintains a loading state during the initial check so that child components
 * do not inadvertently render as 'logged out' before the check is complete.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The child components to render when not loading.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Analytics alleen na expliciete cookietoestemming (initAnalytics gate)
    initAnalytics();

    const unsubscribe = subscribeToAuthChanges((user) => {
      setUser(user);
      setLoading(false);

      if (user) {
        Sentry.setUser({
          id: user.uid,
          email: user.email,
        });
      } else {
        Sentry.setUser(null);
      }
    });

    return unsubscribe;
  }, []);

  const refreshUser = useCallback(async () => {
    const refreshed = await reloadCurrentUser();
    if (refreshed) {
      setUser(refreshed);
    }
    return refreshed;
  }, []);

  const value = {
    user,
    loading,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading ? children : null}
    </AuthContext.Provider>
  );
}
